import { createContext, memo, useContext, useRef, useState } from 'react';
import { noop } from 'lodash';

interface SubmitSubscriptionContextProps {
    triggerSubmission: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subscribe: (callback: () => void | Promise<any>) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    unsubscribe: (callback: () => void | Promise<any>) => void;
    triggeredSubmissionsCount: number;
}

const contextInitialValue: SubmitSubscriptionContextProps = {
    triggerSubmission: noop,
    subscribe: noop,
    unsubscribe: noop,
    triggeredSubmissionsCount: 0,
};

const SubmitSubscriptionContext = createContext<SubmitSubscriptionContextProps>(contextInitialValue);

const SubmitSubscription: React.FC<React.PropsWithChildren<AnyObject>> = ({ children }) => {
    const subscribersRef = useRef<(() => void | Promise<void>)[]>([]);
    const [triggeredSubmissionsCount, setTriggeredSubmissionsCount] = useState(0);

    const triggerSubmission = () => {
        setTriggeredSubmissionsCount((prev) => (prev += 1));
        subscribersRef.current.forEach((callback) => {
            try {
                callback();
            } catch (error) {
                console.error('Error in subscription callback:', error);
            }
        });
    };

    const subscribe = (callback: () => void | Promise<void>) => {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }
        subscribersRef.current.push(callback);
    };

    const unsubscribe = (callback: () => void | Promise<void>) => {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }
        subscribersRef.current = subscribersRef.current.filter((cb) => cb !== callback);
    };

    return (
        <SubmitSubscriptionContext.Provider
            value={{ triggerSubmission, subscribe, unsubscribe, triggeredSubmissionsCount }}
        >
            {children}
        </SubmitSubscriptionContext.Provider>
    );
};

export const useSubmitSubscription = () => {
    const context = useContext(SubmitSubscriptionContext);

    if (!context) return contextInitialValue;

    return context;
};

export const SubmitSubscriptionProvider = memo(SubmitSubscription);
